import React, { useEffect, useState, useCallback } from 'react';
import { Shield, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

const ACTION_OPTIONS = ['', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'DISPATCH', 'DELIVER', 'CONFIRM', 'CANCEL'];
const TARGET_OPTIONS = ['', 'SHIPMENT', 'PAYMENT', 'USER', 'BRANCH', 'COMPANY'];
const LIMIT = 25;

const ACTION_COLORS: Record<string, string> = {
  CREATE: '#22c55e', UPDATE: '#3b82f6', DELETE: '#ef4444',
  LOGIN: '#6366f1', LOGOUT: '#94a3b8', DISPATCH: '#0ea5e9',
  DELIVER: '#22c55e', CONFIRM: '#f59e0b', CANCEL: '#ef4444',
};

export const CompanyAdminAuditLog: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const params: any = { companyId: user.companyId, limit: LIMIT, offset: page * LIMIT };
      if (actionFilter) params.action = actionFilter;
      if (targetFilter) params.targetType = targetFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (search) params.search = search;
      const res = await apiClient.getAuditLogs(params);
      const d = res.data.data;
      setLogs(d?.logs ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [user, page, actionFilter, targetFilter, dateFrom, dateTo, search]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={20} /> Audit Log
        </h1>
        <span style={{ fontSize: 13, color: '#64748b' }}>{total} total events</span>
      </div>

      <div className="card">
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search actor, target..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
          <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} style={{ width: 150 }}>
            {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a || 'All Actions'}</option>)}
          </select>
          <select value={targetFilter} onChange={e => { setTargetFilter(e.target.value); setPage(0); }} style={{ width: 150 }}>
            {TARGET_OPTIONS.map(t => <option key={t} value={t}>{t || 'All Targets'}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 145 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 145 }} />
        </div>

        {loading ? <div className="loading-spinner">Loading audit log...</div> : logs.length === 0 ? (
          <div className="empty-state">
            <Shield size={32} color="#cbd5e1" style={{ marginBottom: 12 }} />
            <h3>No audit events found</h3>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Action</th><th>Actor</th><th>Role</th><th>Target</th><th>Details</th><th>IP</th><th>Time</th></tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => (
                    <tr key={log.id}>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: `${ACTION_COLORS[log.action] || '#94a3b8'}18`,
                          color: ACTION_COLORS[log.action] || '#64748b',
                        }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 13 }}>
                        {log.actorName || log.actor?.firstName
                          ? `${log.actor?.firstName ?? ''} ${log.actor?.lastName ?? ''}`.trim() || log.actorName
                          : log.actorId?.slice(0, 8) || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{log.actorRole?.replace(/_/g, ' ') || '—'}</td>
                      <td style={{ fontSize: 12 }}>
                        {log.targetType && <span style={{ color: '#475569', fontWeight: 500 }}>{log.targetType}</span>}
                        {log.targetId && <span style={{ color: '#94a3b8' }}> #{log.targetId.slice(0, 8)}</span>}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.description || log.details || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{log.ipAddress || '—'}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#64748b' }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
